// Typed client for portal-api. Auth is the JWT minted by
// accounts.prototypical.ai and handed off to us via the URL fragment
// (see main.tsx). We send it as Authorization: Bearer on every
// request and keep credentials:include so any same-site cookie path
// (e.g. local dev with a proxy) still works.

import { notifySiteChanged } from "../siteEvents";

declare const __API_BASE_URL__: string;
declare const __ACCOUNTS_BASE_URL__: string;

export const API_BASE_URL = __API_BASE_URL__;
export const ACCOUNTS_BASE_URL = __ACCOUNTS_BASE_URL__;

export const accountsHost = () => {
  try {
    return new URL(ACCOUNTS_BASE_URL).host;
  } catch {
    return "accounts.protopal.ai";
  }
};

export function getToken(): string | null {
  try {
    return sessionStorage.getItem("protopal_token");
  } catch {
    return null;
  }
}

export function clearToken(): void {
  try {
    sessionStorage.removeItem("protopal_token");
  } catch {
    /* ignore */
  }
}

function bounceToAccounts(): never {
  const next = encodeURIComponent(window.location.href);
  window.location.href = `${ACCOUNTS_BASE_URL}/?next=${next}`;
  // never returns — but keep TS happy for callers
  throw new Error("redirecting to accounts");
}

export class ApiError extends Error {
  constructor(public status: number, public payload: unknown) {
    super(`API ${status}`);
  }
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const authHeaders: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await fetch(`${API_BASE_URL}${path}`, {
    credentials: "include",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
      ...(init?.headers ?? {}),
    },
  });
  if (res.status === 401) {
    clearToken();
    bounceToAccounts();
  }
  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    throw new ApiError(res.status, payload);
  }
  return (await res.json()) as T;
}

/** Invalidate site views only after the server has accepted a site mutation. */
export async function requestSiteMutation<T>(path: string, init: RequestInit): Promise<T> {
  try {
    const result = await request<T>(path, init);
    notifySiteChanged();
    return result;
  } catch (reason) {
    // Creation may persist a recoverable draft even when starting its build fails.
    const payload = reason instanceof ApiError ? reason.payload as { site?: { id?: unknown } } | null : null;
    if (typeof payload?.site?.id === "string") notifySiteChanged();
    throw reason;
  }
}

export function logout(): void {
  const token = getToken();
  // Revoke THIS app's own session server-side before navigating away.
  // Sessions are stateful (portal-api getUserFromToken checks the
  // user_sessions row), so without this the dashboard's SSO-exchanged
  // token stays valid for 7 days even after the client forgets it.
  // keepalive lets the POST survive the location.replace below.
  if (token) {
    try {
      void fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        keepalive: true,
      }).catch(() => {
        /* best-effort; the client-side clear + accounts revoke are the guarantee */
      });
    } catch {
      /* ignore */
    }
  }
  clearToken();
  // Accounts lives on another origin and owns a separate sessionStorage
  // token. The one-time flag makes it revoke that session + clear the token
  // before showing login (platform/accounts consumeLogoutAtBootstrap).
  window.location.replace(`${ACCOUNTS_BASE_URL}/?logout=1`);
}
